import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = 'text', value, onChange, textarea, testId }) => (
  <label className="block group">
    <span className="block text-[10px] tracking-[0.4em] uppercase text-violet-soft/80 mb-3 font-light">
      {label}
    </span>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={4}
        required
        data-testid={testId}
        className="w-full bg-transparent border-0 border-b border-violet-soft/35 focus:border-violet outline-none py-3 text-violet-soft font-light text-base md:text-lg placeholder:text-violet-soft/30 transition-colors resize-none"
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required
        data-testid={testId}
        className="w-full bg-transparent border-0 border-b border-violet-soft/35 focus:border-violet outline-none py-3 text-violet-soft font-light text-base md:text-lg placeholder:text-violet-soft/30 transition-colors"
      />
    )}
  </label>
);

export const ConnectFooter = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [news, setNews] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.connect.sent);
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast.error(t.connect.error);
    } finally {
      setSending(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!news) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email: news });
      toast.success(t.connect.subscribed);
      setNews('');
    } catch (err) {
      toast.error(t.connect.error);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section
      id="connect"
      data-testid="connect-section"
      className="relative bg-burgundy text-peach overflow-hidden font-body"
    >
      {/* Marquee strip */}
      <div className="border-y border-peach/15 overflow-hidden py-5 md:py-7">
        <div className="marquee-track flex gap-8 md:gap-10 whitespace-nowrap font-display font-medium text-2xl md:text-5xl lg:text-6xl text-violet-soft will-change-transform">
          {Array.from({ length: 4 }).flatMap((_, b) =>
            t.footer.marquee.flatMap((word, i) => [
              <span key={`w-${b}-${i}`}>{word}</span>,
              <span key={`d-${b}-${i}`} className="text-violet/60 text-2xl">·</span>,
            ])
          )}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-12 lg:gap-20">
          <div className="md:col-span-7">
            <span className="text-[11px] tracking-[0.4em] uppercase text-violet-soft font-light block mb-5">
              {t.connect.eyebrow}
            </span>
            <h2 className="font-display font-medium leading-[1] tracking-tight text-6xl md:text-7xl lg:text-8xl text-violet-soft">
              {t.connect.title}
              <span className="text-cherry-glow">.</span>
            </h2>
            <p className="font-display font-medium text-xl md:text-2xl leading-snug text-violet-soft/90 mt-6 max-w-xl">
              {t.connect.lead}
            </p>
          </div>

          <div className="md:col-span-5 md:pt-12">
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <Field label={t.connect.name} name="name" value={form.name} onChange={onChange} testId="contact-name" />
              <Field label={t.connect.email} name="email" type="email" value={form.email} onChange={onChange} testId="contact-email" />
              <Field label={t.connect.message} name="message" value={form.message} onChange={onChange} textarea testId="contact-message" />
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="btn-line group inline-flex items-center gap-4 border-b border-violet pb-2 hover:border-violet-soft text-violet-soft hover:text-violet transition-colors disabled:opacity-50"
              >
                <span className="text-[11px] tracking-[0.4em] uppercase font-light">
                  {sending ? t.connect.sending : t.connect.send}
                </span>
                <span aria-hidden="true" className="font-serif text-2xl group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </form>

            <div className="mt-16 pt-10 border-t border-cherry-glow/25">
              <h3 className="font-display font-medium text-3xl md:text-3xl mb-2 text-cherry-glow">
                {t.connect.newsletterTitle}
              </h3>
              <p className="text-cherry-glow/75 text-sm font-light mb-6 max-w-md">
                {t.connect.newsletterLead}
              </p>
              <form onSubmit={subscribe} className="flex items-end gap-4" data-testid="newsletter-form">
                <div className="flex-1">
                  <input
                    type="email"
                    required
                    value={news}
                    onChange={(e) => setNews(e.target.value)}
                    placeholder="you@email.com"
                    data-testid="newsletter-email"
                    className="w-full bg-transparent border-0 border-b border-cherry-glow/40 focus:border-cherry-glow outline-none py-2 text-cherry-glow font-light placeholder:text-cherry-glow/30"
                  />
                </div>
                <button
                  type="submit"
                  disabled={subscribing}
                  data-testid="newsletter-submit"
                  className="btn-line text-[10px] tracking-[0.4em] uppercase border-b border-cherry-glow pb-1 text-cherry-glow hover:text-cherry-bright hover:border-cherry-bright disabled:opacity-50"
                >
                  {subscribing ? t.connect.subscribing : t.connect.subscribe}
                </button>
              </form>

              <div className="mt-10 text-[10px] tracking-[0.4em] uppercase text-cherry-glow/70 flex flex-col gap-3">
                <div>
                  {t.connect.mailDirect} —{' '}
                  <a
                    href="mailto:katalin@rubedo.info"
                    className="link-underline text-cherry-glow hover:text-cherry-bright normal-case tracking-normal text-sm"
                    data-testid="mail-direct-link"
                  >
                    katalin@rubedo.info
                  </a>
                </div>
                <div>
                  <a
                    href="tel:+436648733772"
                    className="link-underline text-cherry-glow hover:text-cherry-bright normal-case tracking-normal text-sm"
                    data-testid="phone-direct-link"
                  >
                    +43 664 8733772
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-24 md:mt-32 pt-8 border-t border-ink/30 text-[10px] tracking-[0.4em] uppercase text-ink font-light">
          {/* Mobile: © + privacy on one line, "all rights reserved" centred below */}
          <div className="flex items-center justify-between gap-4 md:hidden">
            <span>© {new Date().getFullYear()}</span>
            <Link to="/privacy" className="link-underline text-ink hover:text-cherry-glow" data-testid="footer-privacy-link-mobile">
              {t.nav.privacy}
            </Link>
          </div>
          <div className="md:hidden mt-4 font-display font-medium text-ink text-base uppercase tracking-[0.18em] text-center">
            {t.footer.designed}
          </div>

          {/* Desktop: three-column layout */}
          <div className="hidden md:grid md:grid-cols-3 md:items-center md:gap-4">
            <span className="md:justify-self-start">© {new Date().getFullYear()}</span>
            <span className="font-display font-medium text-ink text-base uppercase tracking-[0.18em] md:justify-self-center text-center">{t.footer.designed}</span>
            <Link to="/privacy" className="link-underline text-ink hover:text-cherry-glow md:justify-self-end" data-testid="footer-privacy-link">
              {t.nav.privacy}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConnectFooter;
